import { create } from 'zustand';
import { ProjectFull, ProjectSummary, StepData } from '../types';
import { indexedDBService } from '../services/indexedDB';
import { generateId, calculateProgress } from '../lib/utils';

interface ProjectStore {
  projects: ProjectSummary[];
  currentProject: ProjectFull | null;
  currentStep: number;
  isLoading: boolean;
  error: Error | null;

  // Actions
  loadProjects: () => Promise<void>;
  loadProject: (projectId: string) => Promise<void>;
  createProject: (name: string, genre: string, author?: string) => Promise<string>;
  updateStep: (stepNum: number, data: StepData) => Promise<void>;
  updateProject: (updates: Partial<ProjectFull>) => Promise<void>;
  deleteProject: (projectId: string) => Promise<void>;
  exportProject: (projectId: string) => Promise<string>;
  importProject: (jsonData: string) => Promise<string>;
  setCurrentStep: (step: number) => void;
}

export const useProjectStore = create<ProjectStore>((set, get) => ({
  projects: [],
  currentProject: null,
  currentStep: 0,
  isLoading: false,
  error: null,

  loadProjects: async () => {
    set({ isLoading: true, error: null });
    try {
      const projects = await indexedDBService.getAllProjects();
      const summaries: ProjectSummary[] = projects.map(p => ({
        projectId: p.projectId,
        projectName: p.projectName,
        author: p.author,
        genre: p.genre,
        status: p.status,
        currentStep: p.currentStep,
        progress: calculateProgress(p.currentStep),
        createdAt: p.createdAt,
        updatedAt: p.updatedAt,
      }));
      set({ projects: summaries, isLoading: false });
    } catch (error) {
      set({ error: error as Error, isLoading: false });
    }
  },

  loadProject: async (projectId: string) => {
    set({ isLoading: true, error: null });
    try {
      const project = await indexedDBService.getProject(projectId);
      if (project) {
        set({
          currentProject: project,
          currentStep: project.currentStep,
          isLoading: false
        });
      } else {
        throw new Error('Project not found');
      }
    } catch (error) {
      set({ error: error as Error, isLoading: false });
    }
  },

  createProject: async (name: string, genre: string, author: string = 'unknown') => {
    set({ isLoading: true, error: null });
    try {
      const projectId = generateId();
      const now = new Date().toISOString();

      const project: ProjectFull = {
        projectId,
        projectName: name,
        author,
        genre,
        status: 'draft',
        currentStep: 0,
        progress: 0,
        createdAt: now,
        updatedAt: now,
        step0: {
          mainGenre: genre,
          subGenres: [],
          materials: [],
          occupations: [],
          characterTypes: [],
          era: '',
          region: '',
          themes: [],
          targetAudience: [],
          tone: []
        },
        metadata: {
          syncStatus: 'pending',
          version: 1
        }
      };

      await indexedDBService.saveProject(project);
      await get().loadProjects();
      set({ isLoading: false });
      return projectId;
    } catch (error) {
      set({ error: error as Error, isLoading: false });
      throw error;
    }
  },

  updateStep: async (stepNum: number, data: StepData) => {
    const { currentProject } = get();
    if (!currentProject) {
      throw new Error('No current project');
    }

    set({ isLoading: true, error: null });
    try {
      const stepKey = `step${stepNum}` as keyof ProjectFull;
      const updated: ProjectFull = {
        ...currentProject,
        [stepKey]: data,
        currentStep: Math.max(currentProject.currentStep, stepNum + 1),
        progress: calculateProgress(Math.max(currentProject.currentStep, stepNum + 1)),
        updatedAt: new Date().toISOString()
      };

      await indexedDBService.saveProject(updated);
      await indexedDBService.saveStep(currentProject.projectId, stepNum, data);

      set({ currentProject: updated, isLoading: false });
      await get().loadProjects(); // Refresh project list
    } catch (error) {
      set({ error: error as Error, isLoading: false });
      throw error;
    }
  },

  updateProject: async (updates: Partial<ProjectFull>) => {
    const { currentProject } = get();
    if (!currentProject) {
      throw new Error('No current project');
    }

    set({ isLoading: true, error: null });
    try {
      const updated: ProjectFull = {
        ...currentProject,
        ...updates,
        updatedAt: new Date().toISOString()
      };

      await indexedDBService.saveProject(updated);
      set({ currentProject: updated, isLoading: false });
      await get().loadProjects();
    } catch (error) {
      set({ error: error as Error, isLoading: false });
      throw error;
    }
  },

  deleteProject: async (projectId: string) => {
    set({ isLoading: true, error: null });
    try {
      await indexedDBService.deleteProject(projectId);
      set((state) => ({
        projects: state.projects.filter(p => p.projectId !== projectId),
        currentProject: state.currentProject?.projectId === projectId ? null : state.currentProject,
        isLoading: false
      }));
    } catch (error) {
      set({ error: error as Error, isLoading: false });
      throw error;
    }
  },

  exportProject: async (projectId: string) => {
    set({ isLoading: true, error: null });
    try {
      const jsonData = await indexedDBService.exportProject(projectId);
      set({ isLoading: false });
      return jsonData;
    } catch (error) {
      set({ error: error as Error, isLoading: false });
      throw error;
    }
  },

  importProject: async (jsonData: string) => {
    set({ isLoading: true, error: null });
    try {
      const projectId = await indexedDBService.importProject(jsonData);
      await get().loadProjects();
      set({ isLoading: false });
      return projectId;
    } catch (error) {
      set({ error: error as Error, isLoading: false });
      throw error;
    }
  },

  setCurrentStep: (step: number) => {
    set({ currentStep: step });
  }
}));
